import { createAsyncThunk } from "@reduxjs/toolkit";
import toArray from "lodash/toArray";

import includes from "lodash/includes";

import { fetchAuthToken } from "@/helpers/fetch-auth-token";
import { sanitizeSurveyAnswersForSubmit } from "@/helpers/private-file-submission";
import { inorderTraversal } from "@/helpers/survey";
import {
  createSurvey,
  getQuestionResult,
  getSurvey,
  getSurveyPage,
  getSurveyResponses,
  updateSurvey,
  verifyRecaptcha
} from "@/models";
import { AppState } from "@/redux/store/store";
import type { Question, SurveyParams } from "@/types";
import {
  setEhqSkipQuestion,
  setSurveyErrors,
  setSurveyPage,
  toggleResponse
} from "./slice";
import isValidEmail from "@/helpers/email-regex";

export const verifyRecaptchaV3 = createAsyncThunk(
  "survey/verifyRecaptchaV3",
  async (
    params: { recaptcha_token: string; action: string },
    { rejectWithValue, getState }
  ) => {
    const { adapter } = getState() as AppState;
    const { baseUrl } = adapter.adapter;
    const { recaptcha_token, action } = params;
    const { token } = fetchAuthToken(getState());

    try {
      const response = await verifyRecaptcha(
        token,
        baseUrl,
        recaptcha_token,
        action,
        "v3"
      );

      // Check if verification was successful
      if (!response.success) {
        return rejectWithValue({
          message: "reCAPTCHA verification failed",
          errorCodes: response["error-codes"]
        });
      }

      // Return the full response including score
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchSurvey = createAsyncThunk(
  "survey/fetchSurvey",
  async (
    params: Partial<SurveyParams> & { config?: object },
    { rejectWithValue, getState }
  ) => {
    const { adapter } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const { projectId, surveyId, config } = params;
    const { token } = fetchAuthToken(getState());

    try {
      const response = await getSurvey(
        token,
        baseUrl,
        projectId,
        surveyId,
        config
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchSurveyPage = createAsyncThunk(
  "survey/fetchSurveyPage",
  async (
    params: Partial<SurveyParams> & { config?: object },
    { rejectWithValue, getState }
  ) => {
    const { adapter } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const { projectId, surveyId, config } = params;
    const { token } = fetchAuthToken(getState());

    try {
      const response = await getSurveyPage(
        token,
        baseUrl,
        projectId,
        surveyId,
        config
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const saveSurvey = createAsyncThunk(
  "survey/saveSurvey",
  async (
    params: { query: Partial<SurveyParams>; recaptchaRef; isReCaptchaEnabled },
    { rejectWithValue, getState, dispatch }
  ) => {
    const { survey, adapter, user } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const { query, recaptchaRef, isReCaptchaEnabled } = params;
    const { projectId, surveyId } = query;
    const { registeredUser } = user;
    let recaptchaParam = {};

    const isUserAdmin = registeredUser.profile?.roleName?.indexOf("admin") > -1;
    const isPreviewUser = user?.type === "PreviewUser";

    const {
      survey: currentSurvey,
      answers,
      pages,
      questions,
      options,
      statements,
      saveError,
      responses: surveyResponses
    } = survey;

    const valueBasedQuestions = [
      "FileQuestion",
      "DateQuestion",
      "NumberQuestion",
      "EmailQuestion",
      "EssayQuestion",
      "RegionQuestion",
      "SingleLineQuestion"
    ];

    const idBasedQuestions = [
      "EmojiQuestion",
      "RankingQuestion",
      "CheckBoxQuestion",
      "DropDownQuestion",
      "RadioButtonQuestion"
    ];

    try {
      if (saveError) return {};

      const currentPage = pages.find((page) => page.current);

      const questionsInPage = toArray(questions).filter(
        (q) => q.pageId === currentPage.id
      );
      const optionsInPage = toArray(options).filter((o) =>
        questionsInPage.some((q) => q.id === o.questionId)
      );
      const currentPageAnswers = questionsInPage.reduce((acc, { id }) => {
        acc[id] = answers[id];
        return acc;
      }, {});

      const questionsToBeValidated = inorderTraversal(
        questionsInPage,
        optionsInPage,
        answers
      );

      for (let i = 0; i < questionsToBeValidated.length; i++) {
        const question = questionsToBeValidated[i];
        const { id, type, required } = question;

        const questionOption = toArray(options).filter(
          (o) => o.questionId === id
        );

        if (type === "EssayQuestion" && answers[id]?.value?.length > 20_000) {
          dispatch(
            setSurveyErrors({
              questionId: id,
              error: { code: "maxChars" }
            })
          );
          return;
        }

        if (
          type === "EmailQuestion" &&
          answers[id]?.value &&
          !isValidEmail(answers[id]?.value)
        ) {
          dispatch(
            setSurveyErrors({
              questionId: id,
              error: { code: "invalidEmail" }
            })
          );
          return;
        }

        /**
         * Validates dropdown & radio button questions
         * checks if other option is answered
         * @returns `blank` error code
         */
        if (
          answers[id]?.id &&
          (type === "DropDownQuestion" || type === "RadioButtonQuestion")
        ) {
          const options = questionOption.filter((o) => o.supportsTextAnswer);
          if (options[0]?.id === answers[id]?.id && !answers[id]?.value) {
            dispatch(
              setSurveyErrors({
                questionId: question.id,
                error: { code: "blank" }
              })
            );
            return;
          }
        }

        /**
         * Validates checkbox question
         * checks if other option is answered
         * @returns `blank` error code
         */
        if (answers[id]?.id && type === "CheckBoxQuestion") {
          const op = questionOption.filter((o) => o.supportsTextAnswer);
          if (answers[id]?.id.indexOf(op[0]?.id) > -1 && !answers[id]?.value) {
            dispatch(
              setSurveyErrors({
                questionId: question.id,
                error: { code: "blank" }
              })
            );
            return;
          }
        }

        const validateAnswers = (question: Question) => {
          const { id, type } = question;
          /**
           * Validates value based question answers
           * @returns `blank` error code
           */
          if (includes(valueBasedQuestions, type) && !answers[id]?.value) {
            /**
             * Validate and pass if the input number is 0
             */
            if (type === "NumberQuestion" && answers[id]?.value === 0) return;
            return { code: "blank" };
          }

          /**
           * Validates id based question answers
           * @returns `blank` error code
           */
          if (includes(idBasedQuestions, type) && !answers[id]?.id) {
            return { code: "blank" };
          }

          /**
           * Validates custom answers stored in id based questions
           */
          if (includes(idBasedQuestions, type) && answers[id]?.id) {
            /**
             * Validates ranking question
             * checks if all options are answered
             * @returns `selectOneOption` error code
             */
            if (
              type === "RankingQuestion" &&
              questionOption.length !== answers[id]?.id.length
            ) {
              return { code: "selectOneOption" };
            }
          }

          /**
           * Validates Likert question
           */
          if (type === "LikertQuestion") {
            /**
             * Validates if Likert question is answered
             * @returns `blank` error code
             */
            if (!answers[id]) {
              return { code: "blank" };
            }

            const questionStatement = toArray(statements).filter(
              (qs) => qs.parentId === parseInt(id)
            );

            /**
             * Validates if all Likert question options are answered
             * @returns `selectOneOption` error code
             */
            if (questionStatement.length !== Object.keys(answers[id]).length) {
              return { code: "selectOneOption" };
            }
          }
        };

        if (required) {
          const questionAnswerError = validateAnswers(question);
          if (questionAnswerError) {
            dispatch(
              setSurveyErrors({
                questionId: question.id,
                error: questionAnswerError
              })
            );
          }
        }
      }

      const { errors } = (getState() as AppState).survey;
      if (Object.keys(errors).length) return;

      let triggeredSkip: any = {};
      if (currentPage.triggerEndOfSurvey) {
        triggeredSkip = {
          type: "EndOfSurvey"
        };
      } else if (currentPage.triggerSectionId) {
        triggeredSkip = {
          type: "Page"
        };
      } else {
        for (let i = 0; i < questionsInPage.length; i++) {
          const question = questionsInPage[i];
          if (
            (question.type === "RadioButtonQuestion" ||
              question.type === "DropDownQuestion") &&
            !!answers[question.id]
          ) {
            const option = toArray(options).filter(
              (o) => o.id === answers[question.id].id
            );
            if (option[0].triggerEndOfSurvey) {
              triggeredSkip = {
                type: "EndOfSurvey"
              };
              break;
            } else if (option[0].triggerQuestionId) {
              triggeredSkip = {
                type: "Question",
                question: questions[option[0].triggerQuestionId]
              };
              break;
            }
          }
        }
      }

      if (isReCaptchaEnabled) {
        const recaptchaToken = await recaptchaRef.current.executeAsync();
        recaptchaParam = {
          gRecaptchaResponse: recaptchaToken
        };
        recaptchaRef.current.reset();
      }

      const surveyData = {
        answers: sanitizeSurveyAnswersForSubmit(
          registeredUser?.isLoggedIn ? currentPageAnswers : answers,
          questions
        ),
        pageId: currentPage.id,
        finalized:
          (triggeredSkip && triggeredSkip.type === "EndOfSurvey") ||
          currentPage.last ||
          false,
        ...recaptchaParam
      };

      let response = {};

      if ((isUserAdmin && currentSurvey.state === "draft") || isPreviewUser) {
        // Proceed further in survey without creating a survey
      } else {
        const { token } = fetchAuthToken(getState());

        if (
          Object.prototype.hasOwnProperty.call(
            surveyResponses,
            "completedResponse"
          ) &&
          !surveyResponses?.completedResponse &&
          registeredUser?.isLoggedIn
        ) {
          response = await updateSurvey(
            token,
            baseUrl,
            projectId,
            surveyId,
            surveyResponses?.id,
            surveyData
          );
        } else if (!registeredUser?.isLoggedIn && !surveyData.finalized) {
          // Proceed further in survey without creating a survey
        } else {
          response = await createSurvey(
            token,
            baseUrl,
            projectId,
            surveyId,
            surveyData
          );
        }
      }

      if (!currentPage.last) {
        if (!Object.keys(triggeredSkip).length) {
          dispatch(
            setSurveyPage({
              pageId: currentPage.next
            })
          );
        } else if (triggeredSkip.type === "Page") {
          const pageId = pages.find(
            (page) => page.id === `${currentPage.triggerSectionId}`
          )
            ? currentPage.triggerSectionId
            : currentPage.next;

          dispatch(setSurveyPage({ pageId }));
        } else if (
          triggeredSkip.type === "Question" &&
          triggeredSkip.question
        ) {
          const nextQuestion = triggeredSkip.question;
          toArray(questions).forEach((q) => {
            if (
              q.pageId === nextQuestion.pageId &&
              q.sequence < nextQuestion.sequence
            ) {
              dispatch(setEhqSkipQuestion(q.id));
            }
          });
          dispatch(
            setSurveyPage({
              pageId: nextQuestion.pageId
            })
          );
        } else if (
          triggeredSkip.type === "Question" &&
          !triggeredSkip.question
        ) {
          dispatch(
            setSurveyPage({
              pageId: currentPage.next
            })
          );
        }
      }

      if (surveyData.finalized) dispatch(toggleResponse());
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchSurveyResponses = createAsyncThunk(
  "survey/fetchSurveyResponses",
  async (
    params: Partial<SurveyParams> & { config?: object },
    { rejectWithValue, getState }
  ) => {
    const { adapter } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const { projectId, surveyId, config } = params;
    const { token } = fetchAuthToken(getState());

    try {
      const response = await getSurveyResponses(
        token,
        baseUrl,
        projectId,
        surveyId,
        "current",
        config
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchQuestionResult = createAsyncThunk(
  "survey/fetchQuestionResult",
  async (
    params: {
      projectId: unknown;
      surveyId: unknown;
      pageId: string;
      questionId: string;
    },
    { rejectWithValue, getState }
  ) => {
    const { adapter } = getState() as AppState;

    const { baseUrl } = adapter.adapter;

    const { projectId, surveyId, pageId, questionId } = params;
    const { token } = fetchAuthToken(getState());

    try {
      const response = await getQuestionResult(
        token,
        baseUrl,
        projectId,
        surveyId,
        pageId,
        questionId
      );
      return { [questionId]: response.results };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
